import { t } from "@travily/i18n";
import { sectionTab, sectionTabList } from "@ui/components/SectionTabs";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { useReducedMotion } from "motion/react";
import { useEffect, useRef } from "react";

const sectionJumpNav = tv({
  // Solid background: the strip is designed to pin (`sticky top-0`) inside a
  // scroll container, so content must slide under it. It carries no outer
  // spacing of its own — while pinned it should hug the chrome above it, so
  // any gap to the preceding content belongs on that sibling (e.g. `pb-3`),
  // where it scrolls away naturally.
  base: "bg-white",
});

export interface SectionJumpSection {
  key: string;
  label: string;
}

export interface SectionJumpNavProps {
  sections: readonly SectionJumpSection[];
  /** Key of the section currently in view; drives the underline and `aria-current`. */
  activeKey: string | null;
  /** Called with a section's key when the reader asks to jump to it. */
  onSelect: (key: string) => void;
  /** Accessible name for the nav landmark; defaults to the localized "Page sections". */
  "aria-label"?: string;
  className?: string;
}

/**
 * Quick-jump strip for in-page sections: the mobile evolution of `SectionTabs`
 * where the items scroll the reader to stacked sections instead of swapping tab
 * panels. Deliberately a `<nav>` of buttons, not a tablist — no content is
 * hidden — with `aria-current="location"` marking the section in view.
 *
 * Visuals come from the hoisted `sectionTabList`/`sectionTab` recipes (the
 * `data-selected` contract described there). Controlled: pair `activeKey`/
 * `onSelect` with `useScrollSpy`.
 *
 * The strip keeps the active item in view, scrolling its own list
 * horizontally as `activeKey` changes.
 */
export function SectionJumpNav({
  sections,
  activeKey,
  onSelect,
  className,
  "aria-label": ariaLabel,
}: SectionJumpNavProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const hasPositionedRef = useRef(false);
  const reducedMotion = useReducedMotion() ?? false;

  useEffect(() => {
    if (!activeKey) return;
    const list = listRef.current;
    // The active item carries `data-selected` (rendered by the time effects run).
    const item = list?.querySelector<HTMLElement>("[data-selected]");
    if (!list || !item) return;
    // Manual scrollTo rather than scrollIntoView({ inline }) — the latter may
    // also scroll vertical ancestors, fighting an in-flight section jump.
    const listRect = list.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const centered = itemRect.left - listRect.left + list.scrollLeft - (list.clientWidth - itemRect.width) / 2;
    const max = list.scrollWidth - list.clientWidth;
    // The first positioning happens without any interaction (initial render,
    // restored scroll position) — snap instead of animating.
    const behavior = reducedMotion || !hasPositionedRef.current ? "instant" : "smooth";
    hasPositionedRef.current = true;
    list.scrollTo({ behavior, left: Math.min(Math.max(centered, 0), max) });
  }, [activeKey, reducedMotion]);

  return (
    <nav aria-label={ariaLabel ?? t("components.section_jump_nav.nav_label")} className={sectionJumpNav({ className })}>
      <div className={sectionTabList({ size: "sm" })} ref={listRef}>
        {sections.map(({ key, label }) => {
          const isActive = key === activeKey;
          return (
            <Button
              aria-current={isActive ? "location" : undefined}
              className={sectionTab({ size: "sm" })}
              key={key}
              onPress={() => onSelect(key)}
              {...(isActive ? { "data-selected": true } : {})}
            >
              {label}
            </Button>
          );
        })}
      </div>
    </nav>
  );
}
